import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, FileSignature, ClipboardList, HelpCircle, Megaphone, Download, Eye, FolderPlus, Tag, X, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { CommunicationType, CommunicationStatus, commDate, jobCommsPath } from "./shared";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const TAGS = ["general", "commercial", "instruction", "drawing", "delivery", "approval", "meeting", "rfi"];
const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const fileUrl = (p) => `${API_BASE}/api/files/view?path=${encodeURIComponent(p)}&auth=${encodeURIComponent(localStorage.getItem("fl_token") || "")}`;

export function CommViewer({ comm, onBack, onChanged, onSelect, embedded = false }) {
  const [tagOpen, setTagOpen] = useState(false);
  const toggleTag = async (t) => {
    const tags = comm.tags?.includes(t) ? comm.tags.filter((x) => x !== t) : [...(comm.tags || []), t];
    try { const { data } = await api.patch(`/communications/${comm.id}`, { tags }); onChanged(data); }
    catch { toast.error("Failed to tag"); }
  };
  const saveDoc = async (att) => {
    try {
      await api.post(`/communications/${comm.id}/attachments/${att.id}/save-to-documents`, { category: "other" });
      toast.success(`Saved ${att.filename} to job documents`);
      const { data } = await api.get(`/communications/${comm.id}`); onChanged(data);
    } catch { toast.error("Failed to save to documents"); }
  };
  return (
    <div className={`${embedded ? "" : "border border-zinc-200 dark:border-white/10 rounded-lg p-4"} min-w-0 break-words [overflow-wrap:anywhere]`} data-testid="comm-viewer">
      {!embedded && <button onClick={onBack} className="md:hidden inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2" data-testid="comm-viewer-back">
        <ArrowLeft className="w-3 h-3" /> Back
      </button>}
      <div className="flex flex-wrap items-center gap-2 mb-3"><CommunicationType type={comm.type} /><CommunicationStatus status={comm.status} /></div>
      {comm.job_number || comm.job_title ? <Link className="block text-xs font-semibold text-[#FF5F15] mb-2 hover:underline" to={jobCommsPath(comm.job_id)}>{[comm.job_number, comm.job_title].filter(Boolean).join(" · ")}</Link> : null}
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <h3 className="font-display font-black uppercase tracking-tight text-base leading-tight break-words">{comm.subject}</h3>
          <div className="text-xs text-zinc-500 mt-1">
            <span className="font-bold text-zinc-700 dark:text-zinc-300">{comm.sender_name}</span>
            {comm.sender_company && <span> · {comm.sender_company}</span>}
            <span className="font-mono"> · {comm.sender_email}</span>
          </div>
          <div className="text-[11px] text-zinc-500 mt-0.5">
            {comm.to?.length > 0 && <span>To: {comm.to.join(", ")}</span>}
            {comm.cc?.length > 0 && <span> · CC: {comm.cc.join(", ")}</span>}
          </div>
          <div className="text-[11px] font-mono text-zinc-500 mt-0.5">{new Date(comm.received_at).toLocaleString("en-NZ")}</div>
        </div>
        <CreateFromEmail comm={comm} onChanged={onChanged} inline={embedded} />
      </div>

      {/* tags */}
      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
        {(comm.tags || []).map((t) => (
          <span key={t} className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider bg-[#FF5F15]/10 text-[#FF5F15] border border-[#FF5F15]/30 rounded px-1.5 py-0.5">
            {t}<button onClick={() => toggleTag(t)}><X className="w-2.5 h-2.5" /></button>
          </span>
        ))}
        <div className="relative">
          <button onClick={() => setTagOpen((o) => !o)} className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-zinc-500 border border-dashed border-zinc-400 rounded px-1.5 py-0.5" data-testid="comm-add-tag">
            <Tag className="w-2.5 h-2.5" /> Tag
          </button>
          {tagOpen && (
            <div className="absolute z-20 mt-1 bg-white dark:bg-[#0B0F14] border border-zinc-300 dark:border-white/15 rounded-md shadow-lg p-1.5 flex flex-wrap gap-1 w-[220px]">
              {TAGS.map((t) => (
                <button key={t} onClick={() => { toggleTag(t); setTagOpen(false); }}
                  className={`text-[9px] font-bold uppercase tracking-wider rounded px-1.5 py-0.5 border ${comm.tags?.includes(t) ? "bg-[#FF5F15]/10 text-[#FF5F15] border-[#FF5F15]/40" : "text-zinc-500 border-zinc-300 dark:border-white/15"}`}>{t}</button>
              ))}
            </div>
          )}
        </div>
      </div>

      {(comm.related_records || comm.linked || []).length > 0 && <div className="mt-3 space-y-2">
        <div className="label-tag">Related records</div>
        {(comm.related_records || comm.linked).map((record, i) => {
          const tab = { document: "documents", task: "work", variation: "commercial" }[record.kind];
          return <div key={i} className="border-l-2 border-[#FF5F15]/40 pl-2 text-xs">
            <div className="font-semibold">{tab ? <Link className="hover:underline text-[#FF5F15]" to={`/app/jobs/${comm.job_id}/${tab}`}>{record.label || record.kind}</Link> : (record.label || record.kind?.replaceAll("_", " "))}{record.status && <span className="ml-2 text-zinc-500 capitalize">{record.status}</span>}</div>
            {record.title && <p className="mt-0.5 font-medium">{record.title}</p>}
            {record.description && <p className="mt-1 whitespace-pre-wrap">{record.description}</p>}
            {(record.raised_by || record.issued_by) && <p className="text-zinc-500">{record.raised_by ? "Raised" : "Issued"} by {record.raised_by || record.issued_by}</p>}
            {record.contact_email && <p className="text-zinc-500">Contact: {record.contact_email}</p>}
            {record.created_at && <p className="text-zinc-500">{commDate(record.created_at)}</p>}
          </div>;
        })}
      </div>}

      {/* body */}
      <div className="mt-3 pt-3 border-t border-zinc-200 dark:border-white/10 max-h-[400px] overflow-auto">
        {comm.body_html
          ? <div className="prose prose-sm dark:prose-invert max-w-none text-sm [&_a]:text-[#FF5F15]" dangerouslySetInnerHTML={{ __html: comm.body_html }} />
          : <pre className="text-sm whitespace-pre-wrap font-sans leading-relaxed">{comm.body_text || "(no body)"}</pre>}
      </div>

      {/* attachments */}
      {(comm.attachments || []).length > 0 && (
        <div className="mt-3 pt-3 border-t border-zinc-200 dark:border-white/10">
          <div className="label-tag mb-1.5">Attachments ({comm.attachments.length})</div>
          {comm.attachments.map((a) => (
            <div key={a.id} className="flex items-center gap-2 py-1.5 text-xs flex-wrap" data-testid={`comm-att-${a.id}`}>
              <Paperclip className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
              <span className="font-bold truncate min-w-0">{a.filename}</span>
              <span className="text-zinc-500 font-mono">{fmtSize(a.size)}</span>
              <span className="flex gap-2 ml-auto">
                <a href={fileUrl(a.storage_path)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-zinc-500 hover:text-[#FF5F15]"><Eye className="w-3 h-3" /> View</a>
                <a href={`${fileUrl(a.storage_path)}&download=1`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-zinc-500 hover:text-[#FF5F15]"><Download className="w-3 h-3" /> Download</a>
                <button onClick={() => saveDoc(a)} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-[#FF5F15] hover:underline" data-testid={`save-doc-${a.id}`}><FolderPlus className="w-3 h-3" /> Save to documents</button>
              </span>
            </div>
          ))}
        </div>
      )}
      {comm.thread?.length > 1 && <div className="mt-4 border-t border-zinc-200 dark:border-white/10 pt-3">
        <div className="label-tag mb-2">Conversation · oldest first</div>
        <div className="space-y-1">{comm.thread.map((message) => <button key={message.id} disabled={message.id === comm.id || !onSelect} onClick={() => onSelect(message)} className="block w-full text-left rounded px-2 py-2 text-xs hover:bg-zinc-100 dark:hover:bg-white/5 disabled:bg-[#FF5F15]/5 disabled:opacity-100">
          <span className="block font-semibold">{message.subject}</span><span className="text-zinc-500">{message.sender_name || message.sender_email} · {commDate(message.received_at)}</span>
        </button>)}</div>
      </div>}
      {comm.activity?.length > 0 && <div className="mt-4 border-t border-zinc-200 dark:border-white/10 pt-3">
        <div className="label-tag mb-2">Activity</div>
        <ol className="space-y-1.5 text-[11px] text-zinc-500">{comm.activity.map((event, i) => <li key={i}><span className="font-medium text-zinc-700 dark:text-zinc-300">{event.action?.replaceAll("_", " ")}</span> · {event.user_name || "System"}<span className="block">{commDate(event.at)}</span></li>)}</ol>
      </div>}
    </div>
  );
}

function CreateFromEmail({ comm, onChanged, inline = false }) {
  const [kind, setKind] = useState(null); // 'variation' | 'task' | 'rfi' | 'si'
  const [f1, setF1] = useState("");
  const [f2, setF2] = useState("");
  const [busy, setBusy] = useState(false);
  const open = (k) => {
    setKind(k);
    setF1(comm.subject || "");
    setF2(k === "variation" ? (comm.body_text || "").slice(0, 1500) : "");
    if (k === "variation") { setF1((comm.body_text || comm.subject || "").slice(0, 1500)); setF2(comm.sender_name || ""); }
  };
  const submit = async () => {
    setBusy(true);
    try {
      let url, body, label;
      if (kind === "variation") { url = `/communications/${comm.id}/create-variation`; body = { scope: f1, client_name: f2 }; label = "Variation"; }
      else if (kind === "task") { url = `/communications/${comm.id}/create-task`; body = { title: f1, description: f2 || undefined }; label = "Task"; }
      else if (kind === "rfi") { url = `/communications/${comm.id}/create-rfi`; body = { title: f1, description: f2 || undefined }; label = "RFI"; }
      else { url = `/communications/${comm.id}/create-site-instruction`; body = { title: f1, description: f2 || undefined }; label = "Site instruction"; }
      await api.post(url, body);
      toast.success(`${label} created from this email`);
      const { data } = await api.get(`/communications/${comm.id}`);
      onChanged(data);
      setKind(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create"); }
    finally { setBusy(false); }
  };
  const items = [
    ["variation", "Create Variation", FileSignature],
    ["task", "Create Task", ClipboardList],
    ["rfi", "Create RFI", HelpCircle],
    ["si", "Create Site Instruction", Megaphone],
  ];
  const form = (
          <div className="space-y-2.5 text-sm">
            <div className="text-[11px] text-zinc-500 bg-zinc-100 dark:bg-white/5 rounded-md px-2.5 py-1.5">
              Source: Email · "{comm.subject}" · {new Date(comm.received_at).toLocaleString("en-NZ")}
            </div>
            {kind === "variation" ? (
              <>
                <div><div className="label-tag mb-1">Scope of works</div>
                  <Textarea value={f1} onChange={(e) => setF1(e.target.value)} rows={5} className="text-xs rounded-lg" data-testid="cfe-scope" /></div>
                <div><div className="label-tag mb-1">Client name</div>
                  <Input value={f2} onChange={(e) => setF2(e.target.value)} className="h-9 text-xs rounded-lg" data-testid="cfe-client" /></div>
              </>
            ) : (
              <>
                <div><div className="label-tag mb-1">Title</div>
                  <Input value={f1} onChange={(e) => setF1(e.target.value)} className="h-9 text-xs rounded-lg" data-testid="cfe-title" /></div>
                <div><div className="label-tag mb-1">Description (optional)</div>
                  <Textarea value={f2} onChange={(e) => setF2(e.target.value)} rows={3} className="text-xs rounded-lg" data-testid="cfe-desc" /></div>
              </>
            )}
            <Button onClick={submit} disabled={busy || !f1.trim() || (kind === "variation" && !f2.trim())}
              className="w-full h-9 rounded-lg bg-[#FF5F15] hover:bg-[#E65100] font-bold uppercase tracking-wider text-xs" data-testid="cfe-submit">
              {busy ? "Creating…" : "Create"}
            </Button>
          </div>
  );
  return (
    <>
      <div className="flex flex-col gap-1 shrink-0">
        <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">Create from this email</div>
        {items.map(([k, l, Icon]) => (
          <button key={k} onClick={() => open(k)} data-testid={`create-${k}-btn`}
            className="inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-300 hover:text-[#FF5F15] text-left">
            <Icon className="w-3.5 h-3.5" /> + {l.replace("Create ", "")}
          </button>
        ))}
      </div>
      {inline ? (kind && <div className="w-full basis-full mt-2 border-t border-zinc-200 pt-3">
        <div className="flex justify-between mb-2"><h4 className="font-semibold text-sm">{items.find(([k]) => k === kind)?.[1]}</h4><button onClick={() => setKind(null)} className="text-xs text-zinc-500">Cancel</button></div>
        {form}
      </div>) : <Dialog open={!!kind} onOpenChange={(o) => !o && setKind(null)}>
        <DialogContent className="rounded-lg max-w-md">
          <DialogHeader><DialogTitle className="font-display uppercase">
            {kind === "variation" ? "Create Variation" : kind === "task" ? "Create Task" : kind === "rfi" ? "Create RFI" : "Create Site Instruction"}
          </DialogTitle></DialogHeader>
          {form}
        </DialogContent>
      </Dialog>}
    </>
  );
}
